<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu.app_catalog_storefront") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <!-- ============ Checkout policy — COD pending limit (#181) ============ -->
            <card class="no-border-card">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("checkout_settings.title") }}</h3>
                            <p class="text-sm text-muted mb-0">{{ $t("checkout_settings.subtitle") }}</p>
                        </div>
                    </div>
                </template>

                <div v-loading="checkoutLoading" class="row align-items-end">
                    <div class="col-md-4">
                        <label class="form-control-label">
                            {{ $t("checkout_settings.cod_max_pending_label") }}
                        </label>
                        <el-input-number
                            v-model="checkoutForm.cod_max_pending_orders"
                            :min="0"
                            :max="999"
                            :step="1"
                            :disabled="!isAdmin"
                            controls-position="right"
                            class="d-block"
                        />
                        <small class="form-text text-muted">
                            {{ $t("checkout_settings.cod_max_pending_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3 mt-md-0">
                        <label class="form-control-label">
                            {{ $t("checkout_settings.otp_daily_cap_label") }}
                        </label>
                        <el-input-number
                            v-model="checkoutForm.otp_daily_sms_cap"
                            :min="0"
                            :max="100000"
                            :step="50"
                            :disabled="!isAdmin"
                            controls-position="right"
                            class="d-block"
                        />
                        <small class="form-text text-muted">
                            {{ $t("checkout_settings.otp_daily_cap_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3">
                        <label class="form-control-label">
                            {{ $t("checkout_settings.cod_reject_threshold_label") }}
                        </label>
                        <el-input-number
                            v-model="checkoutForm.cod_reject_block_threshold"
                            :min="0"
                            :max="999"
                            :step="1"
                            :disabled="!isAdmin"
                            controls-position="right"
                            class="d-block"
                        />
                        <small class="form-text text-muted">
                            {{ $t("checkout_settings.cod_reject_threshold_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3">
                        <label class="form-control-label d-block">
                            {{ $t("checkout_settings.points_per_100_label") }}
                        </label>
                        <div class="mb-2">
                            <el-switch
                                v-model="pointsRateEnabled"
                                :disabled="!isAdmin"
                                @change="onPointsRateToggle"
                            ></el-switch>
                            <span class="text-sm text-muted ml-2">
                                {{ $t("checkout_settings.points_rate_custom_toggle") }}
                            </span>
                        </div>
                        <el-input-number
                            v-if="pointsRateEnabled"
                            v-model="pointsRateValue"
                            :min="0"
                            :max="100000"
                            :step="1"
                            :disabled="!isAdmin"
                            controls-position="right"
                            class="d-block"
                            @change="onPointsRateValueChange"
                        />
                        <p v-else class="text-sm text-muted mb-0">
                            {{ $t("checkout_settings.points_rate_unset_label") }}
                        </p>
                        <small class="form-text text-muted">
                            {{ $t("checkout_settings.points_per_100_help") }}
                        </small>
                    </div>
                    <!-- B7 §10.3 (owner decision, 2026-10-02): whether a Shopee
                         promotion that has NOT STARTED yet is advertised on the
                         storefront as "เร็ว ๆ นี้". It is an app setting
                         (mdfpf_app_setting key `promo_show_upcoming`, default
                         false) rather than a config key, because the owner asked
                         for a switch they can flip here instead of a roll.
                         It rides on the checkout-settings PUT — the same generic
                         app-setting endpoint the three numbers above use. -->
                    <div class="col-md-4 mt-3">
                        <label class="form-control-label d-block">
                            {{ $t("shopee_promotion.setting_label") }}
                        </label>
                        <el-switch
                            v-model="checkoutForm.promo_show_upcoming"
                            :disabled="!isAdmin"
                        ></el-switch>
                        <small class="form-text text-muted">
                            {{ $t("shopee_promotion.setting_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3">
                        <base-button
                            v-if="isAdmin"
                            type="primary"
                            :loading="checkoutSaving"
                            @click="saveCheckoutSettings"
                        >
                            <i class="fa fa-save"></i>
                            {{ $t("checkout_settings.btn_save") }}
                        </base-button>
                    </div>
                </div>
            </card>

            <!-- ============ Cash Back — shop-level default + master switch (#654) ============ -->
            <!-- Its own card and its own save button on purpose: the checkout card
                 above PUTs a different endpoint, and mixing the two would make one
                 button write two policies. -->
            <card class="no-border-card">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("cashback_settings.title") }}</h3>
                            <p class="text-sm text-muted mb-0">{{ $t("cashback_settings.subtitle") }}</p>
                        </div>
                    </div>
                </template>

                <div v-loading="cashbackLoading" class="row align-items-end">
                    <div class="col-md-4">
                        <label class="form-control-label d-block">
                            {{ $t("cashback_settings.enabled_label") }}
                        </label>
                        <el-switch v-model="cashbackForm.enabled" :disabled="!isAdmin"></el-switch>
                        <small class="form-text text-muted">
                            {{ $t("cashback_settings.enabled_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3 mt-md-0">
                        <label class="form-control-label">
                            {{ $t("cashback_settings.default_percent_label") }}
                        </label>
                        <el-input-number
                            v-model="cashbackForm.default_percent"
                            :min="0"
                            :max="100"
                            :step="0.5"
                            :precision="2"
                            :disabled="!isAdmin"
                            controls-position="right"
                            class="d-block"
                        />
                        <small class="form-text text-muted">
                            {{ $t("cashback_settings.default_percent_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3">
                        <base-button
                            v-if="isAdmin"
                            type="primary"
                            :loading="cashbackSaving"
                            @click="saveCashbackSettings"
                        >
                            <i class="fa fa-save"></i>
                            {{ $t("cashback_settings.btn_save") }}
                        </base-button>
                    </div>
                    <div class="col-12 mt-3">
                        <small class="form-text text-muted">
                            {{ $t("cashback_settings.hint") }}
                        </small>
                    </div>
                </div>
            </card>

            <!-- ============ ถ้อยคำ coin — customer-facing wording + legal terms (#296) ============ -->
            <!-- Sits under the Cash Back card because it is the same endpoint and the
                 same policy, but keeps its own Save: this one PUTs a PARTIAL body (only
                 the boxes that changed), and folding it into the button above would
                 rewrite the terms text — and bump coin_terms_version — every time
                 somebody adjusted a percentage. -->
            <card class="no-border-card">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("coin_terms.title") }}</h3>
                            <p class="text-sm text-muted mb-0">{{ $t("coin_terms.subtitle") }}</p>
                        </div>
                        <div class="col-auto">
                            <base-button type="secondary" size="sm" @click="openCoinPreview">
                                <i class="fa fa-eye"></i>
                                {{ $t("coin_terms.btn_preview") }}
                            </base-button>
                        </div>
                    </div>
                </template>

                <div v-loading="cashbackLoading">
                    <div class="row">
                        <div class="col-md-6">
                            <label class="form-control-label">{{ $t("coin_terms.name_th_label") }}</label>
                            <el-input v-model="coinTextsForm.coin_name_th" :disabled="!isAdmin" />
                            <small v-if="coinTextsErrors.coin_name_th" class="form-text text-danger">
                                {{ $t("coin_terms.err." + coinTextsErrors.coin_name_th) }}
                            </small>
                            <small v-else class="form-text text-muted">
                                {{ $t("coin_terms.name_help") }} ({{ coinTextLength(coinTextsForm.coin_name_th) }}/{{ coinLimits.name }})
                            </small>
                        </div>
                        <div class="col-md-6 mt-3 mt-md-0">
                            <label class="form-control-label">{{ $t("coin_terms.name_en_label") }}</label>
                            <el-input v-model="coinTextsForm.coin_name_en" :disabled="!isAdmin" />
                            <small v-if="coinTextsErrors.coin_name_en" class="form-text text-danger">
                                {{ $t("coin_terms.err." + coinTextsErrors.coin_name_en) }}
                            </small>
                            <small v-else class="form-text text-muted">
                                {{ $t("coin_terms.name_help") }} ({{ coinTextLength(coinTextsForm.coin_name_en) }}/{{ coinLimits.name }})
                            </small>
                        </div>
                    </div>

                    <div class="row mt-4">
                        <div class="col-md-6">
                            <label class="form-control-label">{{ $t("coin_terms.note_th_label") }}</label>
                            <el-input
                                v-model="coinTextsForm.coin_short_note_th"
                                type="textarea"
                                :rows="2"
                                :disabled="!isAdmin"
                            />
                            <small
                                class="form-text"
                                :class="coinTextOver('coin_short_note_th') ? 'text-danger' : 'text-muted'"
                            >
                                {{ coinTextLength(coinTextsForm.coin_short_note_th) }}/{{ coinLimits.note }}
                            </small>
                            <small v-if="coinTextsErrors.coin_short_note_th" class="form-text text-danger">
                                {{ $t("coin_terms.err." + coinTextsErrors.coin_short_note_th) }}
                            </small>
                        </div>
                        <div class="col-md-6 mt-3 mt-md-0">
                            <label class="form-control-label">{{ $t("coin_terms.note_en_label") }}</label>
                            <el-input
                                v-model="coinTextsForm.coin_short_note_en"
                                type="textarea"
                                :rows="2"
                                :disabled="!isAdmin"
                            />
                            <small
                                class="form-text"
                                :class="coinTextOver('coin_short_note_en') ? 'text-danger' : 'text-muted'"
                            >
                                {{ coinTextLength(coinTextsForm.coin_short_note_en) }}/{{ coinLimits.note }}
                            </small>
                            <small v-if="coinTextsErrors.coin_short_note_en" class="form-text text-danger">
                                {{ $t("coin_terms.err." + coinTextsErrors.coin_short_note_en) }}
                            </small>
                        </div>
                        <div class="col-12">
                            <small class="form-text text-muted">{{ $t("coin_terms.note_help") }}</small>
                            <!-- The two tokens the API substitutes before it serves the
                                 public terms (#694). Spelled out here because a misspelt
                                 token ships as literal braces in a legal sentence. -->
                            <small class="form-text text-muted">{{ $t("coin_terms.placeholder_hint", coinPlaceholderTokens) }}</small>
                        </div>
                    </div>

                    <div class="row mt-4">
                        <div class="col-md-6">
                            <label class="form-control-label">{{ $t("coin_terms.terms_th_label") }}</label>
                            <el-input
                                v-model="coinTextsForm.coin_terms_th"
                                type="textarea"
                                :rows="12"
                                :disabled="!isAdmin"
                            />
                            <small
                                class="form-text"
                                :class="coinTextOver('coin_terms_th') ? 'text-danger' : 'text-muted'"
                            >
                                {{ coinTextLength(coinTextsForm.coin_terms_th) }}/{{ coinLimits.terms }}
                            </small>
                            <small v-if="coinTextsErrors.coin_terms_th" class="form-text text-danger">
                                {{ $t("coin_terms.err." + coinTextsErrors.coin_terms_th) }}
                            </small>
                        </div>
                        <div class="col-md-6 mt-3 mt-md-0">
                            <label class="form-control-label">{{ $t("coin_terms.terms_en_label") }}</label>
                            <el-input
                                v-model="coinTextsForm.coin_terms_en"
                                type="textarea"
                                :rows="12"
                                :disabled="!isAdmin"
                            />
                            <small
                                class="form-text"
                                :class="coinTextOver('coin_terms_en') ? 'text-danger' : 'text-muted'"
                            >
                                {{ coinTextLength(coinTextsForm.coin_terms_en) }}/{{ coinLimits.terms }}
                            </small>
                            <small v-if="coinTextsErrors.coin_terms_en" class="form-text text-danger">
                                {{ $t("coin_terms.err." + coinTextsErrors.coin_terms_en) }}
                            </small>
                        </div>
                        <div class="col-12">
                            <small class="form-text text-muted">{{ $t("coin_terms.terms_help") }}</small>
                            <small class="form-text text-muted">{{ $t("coin_terms.placeholder_hint", coinPlaceholderTokens) }}</small>
                        </div>
                    </div>

                    <div class="row align-items-center mt-4">
                        <div class="col-md-6">
                            <!-- Read-only: the API owns both, and bumps the version itself
                                 whenever the terms text is stored differently (§3). -->
                            <p class="text-sm text-muted mb-0">
                                <span v-if="coinTermsVersion > 0">
                                    {{ $t("coin_terms.version_line", { version: coinTermsVersion, date: coinTermsUpdatedLabel }) }}
                                </span>
                                <span v-else>{{ $t("coin_terms.version_never") }}</span>
                            </p>
                        </div>
                        <div class="col-md-6 mt-3 mt-md-0 text-md-right">
                            <base-button
                                v-if="isAdmin"
                                type="primary"
                                :loading="coinTextsSaving"
                                @click="saveCoinTexts"
                            >
                                <i class="fa fa-save"></i>
                                {{ $t("coin_terms.btn_save") }}
                            </base-button>
                        </div>
                        <div class="col-12 mt-3">
                            <small class="form-text text-muted">{{ $t("coin_terms.hint") }}</small>
                        </div>
                    </div>
                </div>
            </card>

            <!-- ============ เวอร์ชันแอป / บังคับอัปเดต (asrth/mdf-app#205, T-017) ============ -->
            <!-- Its own card and its own Save (one button = one policy). min_version
                 locks customers below it out of the app, so the PUT is admin-only on
                 the API (mw.IsAdmin) and raising a min asks for confirmation first.
                 Logic lives in src/utils/appVersionSettings.js (jest-pinned). -->
            <card class="no-border-card">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("app_version_settings.title") }}</h3>
                            <p class="text-sm text-muted mb-0">{{ $t("app_version_settings.subtitle") }}</p>
                        </div>
                    </div>
                </template>

                <div v-loading="appVersionLoading">
                    <!-- Verifier S1: a failed load must not leave an empty, saveable
                         form — Save sends every field and "" clears it. -->
                    <div v-if="appVersionLoadFailed" class="alert alert-warning d-flex align-items-center" role="alert">
                        <span class="flex-grow-1">{{ $t("app_version_settings.load_failed") }}</span>
                        <base-button size="sm" type="secondary" @click="loadAppVersionSettings">
                            {{ $t("app_version_settings.retry") }}
                        </base-button>
                    </div>
                    <div class="row">
                        <div
                            v-for="p in appVersionPlatforms"
                            :key="p"
                            class="col-md-6"
                            :class="{ 'mt-4 mt-md-0': p === 'ios' }"
                        >
                            <h4 class="mb-3">{{ $t("app_version_settings." + p) }}</h4>
                            <div class="mb-3">
                                <label class="form-control-label">{{ $t("app_version_settings.min_label") }}</label>
                                <el-input
                                    v-model="appVersionForm[p].min_version"
                                    :placeholder="$t('app_version_settings.placeholder_example', { example: '1.5.0' })"
                                    :disabled="!appVersionCanEdit"
                                    clearable
                                />
                                <small class="form-text text-muted">{{ $t("app_version_settings.min_help") }}</small>
                                <small v-if="appVersionErrors[p + '.min_version']" class="form-text text-danger">
                                    {{ $t("app_version_settings.err." + appVersionErrors[p + ".min_version"]) }}
                                </small>
                            </div>
                            <div class="mb-3">
                                <label class="form-control-label">{{ $t("app_version_settings.latest_label") }}</label>
                                <el-input
                                    v-model="appVersionForm[p].latest_version"
                                    :placeholder="$t('app_version_settings.placeholder_example', { example: '1.6.0' })"
                                    :disabled="!appVersionCanEdit"
                                    clearable
                                />
                                <small class="form-text text-muted">{{ $t("app_version_settings.latest_help") }}</small>
                                <small v-if="appVersionErrors[p + '.latest_version']" class="form-text text-danger">
                                    {{ $t("app_version_settings.err." + appVersionErrors[p + ".latest_version"]) }}
                                </small>
                            </div>
                            <div class="mb-3">
                                <label class="form-control-label">{{ $t("app_version_settings.store_url_label") }}</label>
                                <el-input
                                    v-model="appVersionForm[p].store_url"
                                    :placeholder="$t('app_version_settings.placeholder_example', { example: appStoreUrlPlaceholder(p) })"
                                    :disabled="!appVersionCanEdit"
                                    clearable
                                />
                                <small v-if="appVersionErrors[p + '.store_url']" class="form-text text-danger">
                                    {{ $t("app_version_settings.err." + appVersionErrors[p + ".store_url"]) }}
                                </small>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div v-for="lang in ['th', 'en']" :key="lang" class="col-md-6 mb-3">
                            <label class="form-control-label">
                                {{ $t("app_version_settings.message_" + lang + "_label") }}
                            </label>
                            <el-input
                                v-model="appVersionForm['message_' + lang]"
                                type="textarea"
                                :rows="3"
                                :maxlength="appUpdateMessageMax"
                                show-word-limit
                                :disabled="!appVersionCanEdit"
                            />
                            <small v-if="appVersionErrors['message_' + lang]" class="form-text text-danger">
                                {{ $t("app_version_settings.err." + appVersionErrors["message_" + lang]) }}
                            </small>
                        </div>
                    </div>

                    <div class="row align-items-center">
                        <div class="col-md-8">
                            <small class="form-text text-muted">{{ $t("app_version_settings.hint") }}</small>
                        </div>
                        <div class="col-md-4 mt-3 mt-md-0 text-md-right">
                            <base-button
                                v-if="isAdmin"
                                type="primary"
                                :loading="appVersionSaving"
                                :disabled="!appVersionCanSave"
                                @click="saveAppVersionSettings"
                            >
                                <i class="fa fa-save"></i>
                                {{ $t("app_version_settings.btn_save") }}
                            </base-button>
                        </div>
                    </div>
                </div>
            </card>

            <!-- ============ MDFStore sale-channel mapping (order sync source → sale channel) ============ -->
            <card class="no-border-card">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("mdfstore_channel.title") }}</h3>
                            <p class="text-sm text-muted mb-0">{{ $t("mdfstore_channel.subtitle") }}</p>
                        </div>
                    </div>
                </template>

                <div v-loading="mdfChannelLoading" class="row align-items-end">
                    <div class="col-md-4">
                        <label class="form-control-label">
                            {{ $t("mdfstore_channel.source_web") }}
                        </label>
                        <el-select
                            v-model="mdfChannelForm.web_sale_channel_id"
                            :placeholder="$t('mdfstore_channel.select_placeholder')"
                            :disabled="!isAdmin"
                            filterable
                            clearable
                            class="d-block"
                        >
                            <el-option
                                v-for="opt in mdfChannelOptions"
                                :key="opt.id"
                                :label="opt.name"
                                :value="opt.id"
                            ></el-option>
                        </el-select>
                        <small class="form-text text-muted">
                            {{ $t("mdfstore_channel.source_web_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3 mt-md-0">
                        <label class="form-control-label">
                            {{ $t("mdfstore_channel.source_member") }}
                        </label>
                        <el-select
                            v-model="mdfChannelForm.member_sale_channel_id"
                            :placeholder="$t('mdfstore_channel.select_placeholder')"
                            :disabled="!isAdmin"
                            filterable
                            clearable
                            class="d-block"
                        >
                            <el-option
                                v-for="opt in mdfChannelOptions"
                                :key="opt.id"
                                :label="opt.name"
                                :value="opt.id"
                            ></el-option>
                        </el-select>
                        <small class="form-text text-muted">
                            {{ $t("mdfstore_channel.source_member_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3 mt-md-0">
                        <label class="form-control-label">
                            {{ $t("mdfstore_channel.source_app") }}
                        </label>
                        <el-select
                            v-model="mdfChannelForm.app_sale_channel_id"
                            :placeholder="$t('mdfstore_channel.select_placeholder')"
                            :disabled="!isAdmin"
                            filterable
                            clearable
                            class="d-block"
                        >
                            <el-option
                                v-for="opt in mdfChannelOptions"
                                :key="opt.id"
                                :label="opt.name"
                                :value="opt.id"
                            ></el-option>
                        </el-select>
                        <small class="form-text text-muted">
                            {{ $t("mdfstore_channel.source_app_help") }}
                        </small>
                    </div>
                    <div class="col-md-4 mt-3">
                        <base-button
                            v-if="isAdmin"
                            type="primary"
                            :loading="mdfChannelSaving"
                            @click="saveMdfChannelMapping"
                        >
                            <i class="fa fa-save"></i>
                            {{ $t("mdfstore_channel.btn_save") }}
                        </base-button>
                    </div>
                    <div v-if="!mdfChannelLoading && !mdfChannelOptions.length" class="col-12 mt-2">
                        <small class="text-warning">
                            {{ $t("mdfstore_channel.no_options") }}
                        </small>
                    </div>
                </div>
            </card>

            <!-- ============ Hero carousel ============ -->
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-8">
                            <h3 class="mb-0">{{ $t("storefront.hero_title") }}</h3>
                            <p class="text-sm text-muted mb-0">{{ $t("storefront.hero_subtitle") }}</p>
                        </div>
                        <div class="col-4 text-right">
                            <base-button
                                v-if="isAdmin"
                                size="sm"
                                type="primary"
                                @click="openCreateHero"
                            >
                                <i class="fa fa-plus"></i>
                                {{ $t("storefront.btn_add_slide") }}
                            </base-button>
                        </div>
                    </div>
                </template>

                <div v-loading="heroLoading">
                    <el-table
                        :data="heroList"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('storefront.no_slides')"
                    >
                        <el-table-column :label="$t('storefront.col_order')" min-width="110px">
                            <template slot-scope="{ row, $index }">
                                <span class="mr-2">{{ row.sort_order }}</span>
                                <base-button
                                    size="sm"
                                    type="neutral"
                                    class="p-1"
                                    :disabled="$index === 0"
                                    @click="moveUp($index)"
                                >
                                    <i class="fa fa-arrow-up"></i>
                                </base-button>
                                <base-button
                                    size="sm"
                                    type="neutral"
                                    class="p-1"
                                    :disabled="$index === heroList.length - 1"
                                    @click="moveDown($index)"
                                >
                                    <i class="fa fa-arrow-down"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.col_image')" min-width="120px">
                            <template slot-scope="{ row }">
                                <img
                                    v-if="row.image_url"
                                    :src="row.image_url"
                                    style="height: 48px; width: 96px; object-fit: cover; border-radius: 4px;"
                                    alt=""
                                />
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.col_caption_th')" prop="caption_th" min-width="140px">
                            <template slot-scope="{ row }">
                                <span>{{ row.caption_th || "-" }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.col_caption_en')" prop="caption_en" min-width="140px">
                            <template slot-scope="{ row }">
                                <span>{{ row.caption_en || "-" }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.col_link')" prop="link_url" min-width="160px">
                            <template slot-scope="{ row }">
                                <a v-if="row.link_url" :href="row.link_url" target="_blank" rel="noopener">{{ row.link_url }}</a>
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.col_enabled')" min-width="100px">
                            <template slot-scope="{ row }">
                                <el-switch
                                    :value="row.enabled"
                                    :disabled="!isAdmin || row._saving"
                                    @change="toggleHeroEnabled(row)"
                                ></el-switch>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" min-width="120px" align="right">
                            <template slot-scope="{ row }">
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="warning"
                                    class="mr-1"
                                    @click="openEditHero(row)"
                                >
                                    <i class="fa fa-edit"></i>
                                </base-button>
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="danger"
                                    @click="confirmDeleteHero(row)"
                                >
                                    <i class="fa fa-trash"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </card>

            <!-- ============ First-visit popups (multi, single-active) ============ -->
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-8">
                            <h3 class="mb-0">{{ $t("storefront.popup_title") }}</h3>
                            <p class="text-sm text-muted mb-0">{{ $t("storefront.popup_subtitle") }}</p>
                        </div>
                        <div class="col-4 text-right">
                            <base-button
                                v-if="isAdmin"
                                size="sm"
                                type="primary"
                                @click="openCreatePopup"
                            >
                                <i class="fa fa-plus"></i>
                                {{ $t("storefront.btn_add_popup") }}
                            </base-button>
                        </div>
                    </div>
                </template>

                <div v-loading="popupLoading">
                    <el-table
                        :data="popupList"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('storefront.no_popups')"
                    >
                        <el-table-column :label="$t('storefront.col_active')" min-width="90px" align="center">
                            <template slot-scope="{ row }">
                                <el-radio
                                    :value="activePopupId"
                                    :label="row.id"
                                    :disabled="!isAdmin || row._saving"
                                    @change="activatePopup(row)"
                                >
                                    <span></span>
                                </el-radio>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.col_title')" min-width="140px">
                            <template slot-scope="{ row }">
                                <span>{{ row.title || "-" }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.col_image')" min-width="120px">
                            <template slot-scope="{ row }">
                                <img
                                    v-if="row.image_url"
                                    :src="row.image_url"
                                    style="height: 48px; width: 96px; object-fit: cover; border-radius: 4px;"
                                    alt=""
                                />
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.col_link')" prop="link_url" min-width="160px">
                            <template slot-scope="{ row }">
                                <a v-if="row.link_url" :href="row.link_url" target="_blank" rel="noopener">{{ row.link_url }}</a>
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.show_on_www')" min-width="110px" align="center">
                            <template slot-scope="{ row }">
                                <i v-if="row.show_on_www" class="fa fa-check text-success"></i>
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('storefront.show_on_member')" min-width="110px" align="center">
                            <template slot-scope="{ row }">
                                <i v-if="row.show_on_member" class="fa fa-check text-success"></i>
                                <span v-else class="text-muted">-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" min-width="120px" align="right">
                            <template slot-scope="{ row }">
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="warning"
                                    class="mr-1"
                                    @click="openEditPopup(row)"
                                >
                                    <i class="fa fa-edit"></i>
                                </base-button>
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="danger"
                                    @click="confirmDeletePopup(row)"
                                >
                                    <i class="fa fa-trash"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </card>
        </div>

        <!-- ============ Hero create / edit dialog ============ -->
        <el-dialog
            :title="heroEditMode ? $t('storefront.edit_slide') : $t('storefront.btn_add_slide')"
            :visible.sync="heroDialogVisible"
            width="540px"
            @closed="resetHeroForm"
        >
            <el-form
                ref="heroForm"
                :model="heroForm"
                :rules="heroRules"
                label-width="130px"
            >
                <el-form-item :label="$t('storefront.col_image')" prop="image_key">
                    <s3-image-upload
                        object-type="storefront"
                        v-model="heroForm.image_url"
                        @update:objectKey="heroForm.image_key = $event"
                    ></s3-image-upload>
                </el-form-item>
                <el-form-item :label="$t('storefront.col_caption_th')">
                    <el-input v-model="heroForm.caption_th"></el-input>
                </el-form-item>
                <el-form-item :label="$t('storefront.col_caption_en')">
                    <el-input v-model="heroForm.caption_en"></el-input>
                </el-form-item>
                <el-form-item :label="$t('storefront.col_link')">
                    <el-input v-model="heroForm.link_url" placeholder="https://"></el-input>
                </el-form-item>
                <el-form-item :label="$t('storefront.col_order')">
                    <el-input-number v-model="heroForm.sort_order" :min="0"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('storefront.col_enabled')">
                    <el-switch v-model="heroForm.enabled"></el-switch>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="heroDialogVisible = false">{{ $t("cancel") || "Cancel" }}</base-button>
                <base-button type="primary" :loading="heroSaving" @click="submitHeroForm">{{ $t("save") }}</base-button>
            </span>
        </el-dialog>

        <!-- ============ Popup create / edit dialog ============ -->
        <el-dialog
            :title="popupEditMode ? $t('storefront.edit_popup') : $t('storefront.btn_add_popup')"
            :visible.sync="popupDialogVisible"
            width="540px"
            @closed="resetPopupForm"
        >
            <el-form
                ref="popupForm"
                :model="popupForm"
                :rules="popupRules"
                label-width="160px"
            >
                <el-form-item :label="$t('storefront.col_title')">
                    <el-input v-model="popupForm.title" maxlength="64"></el-input>
                </el-form-item>
                <el-form-item :label="$t('storefront.col_image')" prop="image_key">
                    <s3-image-upload
                        object-type="storefront"
                        v-model="popupForm.image_url"
                        @update:objectKey="popupForm.image_key = $event"
                    ></s3-image-upload>
                </el-form-item>
                <el-form-item :label="$t('storefront.col_link')">
                    <el-input v-model="popupForm.link_url" placeholder="https://"></el-input>
                </el-form-item>
                <el-form-item :label="$t('storefront.show_on_www')">
                    <el-switch v-model="popupForm.show_on_www"></el-switch>
                </el-form-item>
                <el-form-item :label="$t('storefront.show_on_member')">
                    <el-switch v-model="popupForm.show_on_member"></el-switch>
                </el-form-item>
                <el-form-item :label="$t('storefront.col_order')">
                    <el-input-number v-model="popupForm.sort_order" :min="0"></el-input-number>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="popupDialogVisible = false">{{ $t("cancel") || "Cancel" }}</base-button>
                <base-button type="primary" :loading="popupSaving" @click="submitPopupForm">{{ $t("save") }}</base-button>
            </span>
        </el-dialog>

        <!-- ===== ตัวอย่างถ้อยคำ coin ที่ลูกค้าเห็น (#296) =====
             Renders the CURRENT form values, saved or not — the point of a preview is
             to read the wording before it becomes the live legal text.
             Paragraphs are real <p> nodes produced by termsParagraphs(); there is no
             v-html anywhere in here, because this text is plain text by design
             (spec §1 "ไม่มี HTML (กัน XSS)") and the same string is rendered to
             customers by member-fn, www and the app. -->
        <el-dialog
            :title="$t('coin_terms.preview_title')"
            :visible.sync="coinPreviewVisible"
            width="720px"
            append-to-body
        >
            <div v-for="pane in coinPreviewPanes" :key="pane.lang" class="mb-4">
                <h5 class="text-uppercase text-muted mb-2">{{ $t(pane.langKey) }}</h5>
                <p class="mb-2">
                    <strong>{{ $t("coin_terms.preview_name_head") }}:</strong>
                    {{ pane.name || $t("coin_terms.preview_empty") }}
                </p>
                <p class="mb-3">
                    <strong>{{ $t("coin_terms.preview_note_head") }}:</strong>
                    {{ pane.note || $t("coin_terms.preview_empty") }}
                </p>
                <h6 class="mb-2">{{ $t("coin_terms.preview_terms_head") }}</h6>
                <div v-if="pane.paragraphs.length" class="coin-terms-preview">
                    <p v-for="(para, idx) in pane.paragraphs" :key="idx">{{ para }}</p>
                </div>
                <p v-else class="text-muted">{{ $t("coin_terms.preview_empty") }}</p>
            </div>
            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="coinPreviewVisible = false">
                    {{ $t("coin_terms.preview_close") }}
                </base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters } from "vuex";
import swal from "sweetalert2";
import { Table, TableColumn, Dialog, Form, FormItem, Input, InputNumber, Switch, Radio, Select, Option } from "element-ui";
import { StorefrontService } from "@/resource";
import { normalizeCashbackPercent, CASHBACK_DEFAULT_PERCENT } from "@/utils/cashbackConfig";
import {
    COIN_TEXT_LIMITS,
    COIN_TEXT_FIELDS,
    coinTextLength,
    coinTextLimitFor,
    coinTermsUpdatedText,
    coinTextsDiff,
    normalizeCoinText,
    renderCoinPlaceholders,
    termsParagraphs,
    validateCoinTexts
} from "@/utils/coinTerms";
// B7 §10.3 — one reader for the `promo_show_upcoming` / `show_upcoming` pair.
import { promoShowUpcoming } from "@/utils/shopeePromotion";
import {
    APP_VERSION_PLATFORMS,
    APP_UPDATE_MESSAGE_MAX,
    APP_STORE_URL_ANDROID_DEFAULT,
    APP_STORE_URL_IOS_PLACEHOLDER,
    emptyAppVersionForm,
    normalizeAppVersionResponse,
    validateAppVersionForm,
    raisedMinVersions,
    raisedMinLabel,
    appVersionPayload,
    canEditAppVersion,
    canSaveAppVersion
} from "@/utils/appVersionSettings";
import S3ImageUpload from "@/components/Inputs/S3ImageUpload.vue";

export default {
    name: "StorefrontSettings",
    components: {
        S3ImageUpload,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Dialog.name]: Dialog,
        [Form.name]: Form,
        [FormItem.name]: FormItem,
        [Input.name]: Input,
        [InputNumber.name]: InputNumber,
        [Switch.name]: Switch,
        [Radio.name]: Radio,
        [Select.name]: Select,
        [Option.name]: Option
    },
    data() {
        return {
            // Checkout policy (COD pending limit #181, OTP daily SMS cap #180)
            // promo_show_upcoming (B7 §10.3) lives on this form because it is
            // stored by the same generic app-setting endpoint; default false,
            // which is the behaviour before the owner's decision.
            checkoutForm: {
                cod_max_pending_orders: 3,
                otp_daily_sms_cap: 500,
                cod_reject_block_threshold: 3,
                promo_show_upcoming: false
            },
            checkoutLoading: false,
            checkoutSaving: false,
            // Points earn rate (#points_per_100_baht) — BE sentinel -1 = unset (tier-based rate), 0 = disabled,
            // >0 = explicit flat rate. Never bind the -1 sentinel into the numeric input, and only send the key
            // on save when the admin actually touched this control (partial PUT — omit means "leave unchanged").
            pointsRateEnabled: false,
            pointsRateValue: 0,
            pointsRateDirty: false,
            // Cash Back (#654) — shop-level master switch + default percentage.
            // Config only in this round: saving these grants nobody a coin, it
            // just records what the coin system will use once item 6 turns it on.
            cashbackForm: { enabled: false, default_percent: CASHBACK_DEFAULT_PERCENT },
            cashbackLoading: false,
            cashbackSaving: false,
            // ถ้อยคำ coin (#296) — the customer-facing unit name, the short note and
            // the full terms, in both languages. Same GET as the Cash Back card above.
            //
            // coinTextsSaved is the snapshot of what the API last reported and is what
            // a Save diffs against: the PUT is partial and must carry ONLY the boxes
            // that changed, or every save would rewrite the terms text and bump
            // coin_terms_version (the number customers re-accept).
            coinTextsForm: {
                coin_name_th: "",
                coin_name_en: "",
                coin_short_note_th: "",
                coin_short_note_en: "",
                coin_terms_th: "",
                coin_terms_en: ""
            },
            coinTextsSaved: {},
            coinTextsErrors: {},
            coinTextsSaving: false,
            coinTermsVersion: 0,
            coinTermsUpdatedAt: "",
            // The numbers the API substitutes into `{months}` / `{max_use}` before it
            // serves the PUBLIC terms (#694). The boxes above hold the RAW template;
            // this is what the preview renders it with, so the operator reads the
            // sentence the customer gets. Empty = the API sent none ⇒ the util's
            // documented fallback (12 / 50) applies.
            coinPlaceholders: {},
            coinPreviewVisible: false,
            // App force / recommend update (asrth/mdf-app#205). appVersionSaved is
            // the last state the API reported — the "did min go up?" confirm diffs
            // the form against it.
            appVersionPlatforms: APP_VERSION_PLATFORMS,
            appUpdateMessageMax: APP_UPDATE_MESSAGE_MAX,
            appVersionForm: emptyAppVersionForm(),
            appVersionSaved: emptyAppVersionForm(),
            appVersionErrors: {},
            appVersionLoading: false,
            // true only after a successful GET in this page visit (S1).
            appVersionLoaded: false,
            appVersionLoadFailed: false,
            appVersionSaving: false,
            // MDFStore sale-channel mapping (order sync source → sale channel)
            mdfChannelForm: { web_sale_channel_id: null, member_sale_channel_id: null, app_sale_channel_id: null },
            mdfChannelOptions: [],
            mdfChannelLoading: false,
            mdfChannelSaving: false,
            // Hero
            heroList: [],
            heroLoading: false,
            heroDialogVisible: false,
            heroEditMode: false,
            heroSaving: false,
            heroForm: this.emptyHeroForm(),
            heroRules: {
                image_key: [{ required: true, message: this.$t("storefront.field_image_required"), trigger: "change" }]
            },
            // Popup (multi-row, single-active #59)
            popupList: [],
            popupLoading: false,
            popupDialogVisible: false,
            popupEditMode: false,
            popupSaving: false,
            popupForm: this.emptyPopupForm(),
            popupRules: {
                image_key: [{ required: true, message: this.$t("storefront.field_image_required"), trigger: "change" }]
            }
        };
    },
    computed: {
        ...mapGetters({
            isAdmin: "Auth/isAdmin"
        }),
        appVersionState() {
            return {
                isAdmin: this.isAdmin === true,
                loaded: this.appVersionLoaded,
                loading: this.appVersionLoading,
                saving: this.appVersionSaving
            };
        },
        appVersionCanEdit() {
            return canEditAppVersion(this.appVersionState);
        },
        appVersionCanSave() {
            return canSaveAppVersion(this.appVersionState);
        },
        activePopupId() {
            const active = this.popupList.find((p) => p.enabled);
            return active ? active.id : null;
        },
        // ---------------- ถ้อยคำ coin (#296) ----------------
        // The limits live in the util (they mirror the API's) — exposed here only so
        // the counters can render "n/300" without hardcoding 300 in the template.
        coinLimits() {
            return COIN_TEXT_LIMITS;
        },
        // The token NAMES, handed to $t as interpolation values rather than written
        // into the message. vue-i18n 8 has no escape for a literal "{" — a `{months}`
        // typed straight into th.js/en.js would be read as a parameter this call does
        // not pass and render as an empty string, i.e. a hint about two tokens that
        // names neither of them.
        coinPlaceholderTokens() {
            return { tok_months: "{months}", tok_max: "{max_use}" };
        },
        coinTermsUpdatedLabel() {
            const text = coinTermsUpdatedText(this.coinTermsUpdatedAt);
            return text === "" ? "-" : text;
        },
        // The two preview panes, built from the CURRENT form (unsaved edits included)
        // and rendered THE WAY THE CUSTOMER SEES IT — which is the only thing a
        // preview is for:
        //   * `{months}` / `{max_use}` are substituted with the numbers the API
        //     reported in `placeholders` (#694). The boxes hold templates; the public
        //     GET /coins/terms serves substituted text, so previewing the template
        //     verbatim would show a `{months}` nobody outside this page ever sees —
        //     and would hide a misspelt `{month}` that will ship as literal braces.
        //   * paragraphs come from termsParagraphs(), whose rule is now identical in
        //     bo-fn, member-fn and www, so the clause count here is the clause count
        //     on the storefront.
        coinPreviewPanes() {
            const ph = this.coinPlaceholders;
            const render = (v) => renderCoinPlaceholders(v, ph);
            return [
                {
                    lang: "th",
                    langKey: "coin_terms.preview_th",
                    name: render(this.coinTextsForm.coin_name_th),
                    note: render(this.coinTextsForm.coin_short_note_th),
                    paragraphs: termsParagraphs(render(this.coinTextsForm.coin_terms_th))
                },
                {
                    lang: "en",
                    langKey: "coin_terms.preview_en",
                    name: render(this.coinTextsForm.coin_name_en),
                    note: render(this.coinTextsForm.coin_short_note_en),
                    paragraphs: termsParagraphs(render(this.coinTextsForm.coin_terms_en))
                }
            ];
        }
    },
    mounted() {
        this.loadCheckoutSettings();
        this.loadCashbackSettings();
        this.loadAppVersionSettings();
        this.loadMdfChannelMapping();
        this.loadHero();
        this.loadPopups();
    },
    methods: {
        // ---------------- Checkout policy (COD pending limit, #181) ----------------
        async loadCheckoutSettings() {
            this.checkoutLoading = true;
            try {
                const res = await StorefrontService.getCheckoutSettings();
                const data = (res.data && res.data.data) || {};
                this.checkoutForm = {
                    cod_max_pending_orders:
                        typeof data.cod_max_pending_orders === "number" ? data.cod_max_pending_orders : 3,
                    otp_daily_sms_cap:
                        typeof data.otp_daily_sms_cap === "number" ? data.otp_daily_sms_cap : 500,
                    cod_reject_block_threshold:
                        typeof data.cod_reject_block_threshold === "number" ? data.cod_reject_block_threshold : 3,
                    // B7 §10.3. promoShowUpcoming() accepts both spellings the
                    // API uses (`promo_show_upcoming` here, `show_upcoming` on
                    // the promotions endpoints) and reads a missing key as false,
                    // so an API that predates the decision shows the switch off
                    // rather than blank.
                    promo_show_upcoming: promoShowUpcoming(data)
                };
                // points_per_100_baht: BE returns -1 (or missing) when the admin has never set a custom
                // rate — the member-tier default rate applies. Never bind -1 into the el-input-number;
                // show it as "unset" instead, and reset the dirty flag so a plain save of the other
                // fields does not accidentally emit this key.
                const pointsRaw = data.points_per_100_baht;
                if (typeof pointsRaw === "number" && pointsRaw >= 0) {
                    this.pointsRateEnabled = true;
                    this.pointsRateValue = pointsRaw;
                } else {
                    this.pointsRateEnabled = false;
                    this.pointsRateValue = 0;
                }
                this.pointsRateDirty = false;
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.checkoutLoading = false;
            }
        },
        onPointsRateToggle() {
            this.pointsRateDirty = true;
            if (this.pointsRateEnabled && (this.pointsRateValue === null || this.pointsRateValue === undefined)) {
                this.pointsRateValue = 0;
            }
        },
        onPointsRateValueChange() {
            this.pointsRateDirty = true;
        },
        async saveCheckoutSettings() {
            this.checkoutSaving = true;
            try {
                const payload = {
                    cod_max_pending_orders: this.checkoutForm.cod_max_pending_orders,
                    otp_daily_sms_cap: this.checkoutForm.otp_daily_sms_cap,
                    cod_reject_block_threshold: this.checkoutForm.cod_reject_block_threshold,
                    // B7 §10.3 — always sent, never conditional: it is a boolean
                    // with no "unset" state (unlike points_per_100_baht), so a
                    // partial PUT that omitted it could not express "turn it off".
                    promo_show_upcoming: this.checkoutForm.promo_show_upcoming === true
                };
                // Only include points_per_100_baht when the admin actually touched this control on this
                // page load — the PUT endpoint is partial, so omitting the key leaves the existing rate
                // (including "unset"/tier-based) untouched. This prevents saving unrelated fields (e.g.
                // COD/OTP) from silently zeroing out the points rate for the whole system.
                if (this.pointsRateDirty) {
                    payload.points_per_100_baht = this.pointsRateEnabled ? this.pointsRateValue : -1;
                }
                await StorefrontService.updateCheckoutSettings(payload);
                this.pointsRateDirty = false;
                this.$notify({ type: "success", message: this.$t("checkout_settings.saved") });
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.checkoutSaving = false;
            }
        },
        // ---------------- Cash Back config (#654) ----------------
        async loadCashbackSettings() {
            this.cashbackLoading = true;
            try {
                const res = await StorefrontService.getCashbackSettings();
                const data = (res.data && res.data.data) || {};
                // The API always answers with a usable pair (it substitutes its
                // own default when no row exists), but the form must survive an
                // older/partial response too — a null in el-input-number renders
                // as an empty box the operator could then save as "nothing".
                const pct = normalizeCashbackPercent(data.default_percent);
                this.cashbackForm = {
                    enabled: data.enabled === true,
                    default_percent: pct === null ? CASHBACK_DEFAULT_PERCENT : pct
                };
                // Same endpoint carries the coin wording (#296 / spec §3): one GET,
                // two sections. An API that predates #296 simply omits the keys, and
                // bindCoinTexts leaves the boxes empty rather than inventing wording.
                this.bindCoinTexts(data);
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.cashbackLoading = false;
            }
        },
        async saveCashbackSettings() {
            // Clearing the number box yields null. Refuse rather than PUT it:
            // the API would reject it as bad_percent anyway, and the operator
            // gets the message next to the field instead of as a server error.
            const pct = normalizeCashbackPercent(this.cashbackForm.default_percent);
            if (pct === null) {
                this.$notify({ type: "danger", message: this.$t("cashback_settings.default_percent_help") });
                return;
            }
            this.cashbackSaving = true;
            try {
                const res = await StorefrontService.updateCashbackSettings({
                    enabled: this.cashbackForm.enabled === true,
                    default_percent: pct
                });
                // Re-bind from the response: the API rounds to two decimals on
                // save, so 12.345 must become the 12.35 it actually stored —
                // leaving the typed value on screen would show a number the
                // database does not hold.
                const data = (res.data && res.data.data) || {};
                const saved = normalizeCashbackPercent(data.default_percent);
                this.cashbackForm = {
                    enabled: data.enabled === true,
                    default_percent: saved === null ? pct : saved
                };
                this.$notify({ type: "success", message: this.$t("cashback_settings.saved") });
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.cashbackSaving = false;
            }
        },
        // ---------------- ถ้อยคำ coin (#296) ----------------
        // The counter measures the NORMALISED value — the same string
        // validateCoinTexts() judges and coinTextsDiff() actually PUTs (#694 item 4).
        // Counting the raw textarea value instead made the number disagree with the
        // rule it exists to explain: a CRLF pasted from Word counted 2 against 8,000
        // where the server counts 1, and a trailing newline kept "8000/8000" red on
        // text the API accepts.
        coinTextLength(v) {
            return coinTextLength(normalizeCoinText(v));
        },
        // Whether one box is ALREADY past its limit, so its counter turns red while
        // the operator types instead of only when they press Save.
        coinTextOver(field) {
            return this.coinTextLength(this.coinTextsForm[field]) > coinTextLimitFor(field);
        },
        // bindCoinTexts adopts one GET/PUT response as both the form and the
        // diff baseline. The two must be the SAME strings: if the form showed the
        // text the operator typed while the baseline held what the server stored,
        // the next Save would re-send text that is already saved.
        bindCoinTexts(data) {
            const d = data || {};
            const form = {};
            const saved = {};
            for (let i = 0; i < COIN_TEXT_FIELDS.length; i++) {
                const field = COIN_TEXT_FIELDS[i];
                const v = d[field];
                // Only a string counts as "the API told us this". Anything else
                // (missing key on an older API, null, a number) leaves the box
                // empty AND leaves the baseline without the key — so typing ''
                // into it still registers as a change worth sending.
                if (typeof v === "string") {
                    form[field] = v;
                    saved[field] = v;
                } else {
                    form[field] = "";
                }
            }
            this.coinTextsForm = form;
            this.coinTextsSaved = saved;
            this.coinTextsErrors = {};
            this.coinTermsVersion =
                typeof d.coin_terms_version === "number" && d.coin_terms_version > 0 ? d.coin_terms_version : 0;
            this.coinTermsUpdatedAt = typeof d.coin_terms_updated_at === "string" ? d.coin_terms_updated_at : "";
            // `placeholders` is read-only and NOT one of COIN_TEXT_FIELDS, so it is
            // never part of a PUT. An API that predates #694 omits it; {} then lets
            // renderCoinPlaceholders fall back to its documented 12 / 50.
            this.coinPlaceholders =
                d.placeholders && typeof d.placeholders === "object" && !Array.isArray(d.placeholders)
                    ? d.placeholders
                    : {};
        },
        openCoinPreview() {
            this.coinPreviewVisible = true;
        },
        async saveCoinTexts() {
            // Refuse locally what the API would answer 400 bad_text to, so the
            // operator sees WHICH box is wrong instead of one server-wide message.
            const errors = validateCoinTexts(this.coinTextsForm);
            this.coinTextsErrors = errors;
            if (Object.keys(errors).length > 0) {
                this.$notify({ type: "danger", message: this.$t("coin_terms.err_form") });
                return;
            }
            // Partial PUT: only the boxes that actually changed. Nothing changed =
            // nothing sent, because a PUT of identical terms text is still a write,
            // and a write is what the API counts when it decides whether this is a
            // new version of the legal wording.
            const payload = coinTextsDiff(this.coinTextsSaved, this.coinTextsForm);
            if (Object.keys(payload).length === 0) {
                this.$notify({ type: "info", message: this.$t("coin_terms.no_change") });
                return;
            }
            this.coinTextsSaving = true;
            try {
                const res = await StorefrontService.updateCashbackSettings(payload);
                // Re-bind from the response: it carries the version the API decided
                // on and the text it really stored (trimmed), so the screen shows the
                // stored document rather than the typed one.
                const data = (res.data && res.data.data) || {};
                this.bindCoinTexts(data);
                this.$notify({ type: "success", message: this.$t("coin_terms.saved") });
            } catch (err) {
                this.notifyCoinTextsError(err);
            } finally {
                this.coinTextsSaving = false;
            }
        },
        // The API's 400 answer for this section is the bare code `bad_text` in Msg
        // (spec §3), which apiErrorMessage would show verbatim — an English token an
        // operator cannot act on. Everything else keeps the shared handling.
        notifyCoinTextsError(err) {
            const data = (err && err.response && err.response.data) || {};
            const raw = ((data.Msg || data.message || "") + "").trim();
            if (raw === "bad_text") {
                this.$notify({ type: "danger", message: this.$t("coin_terms.err_bad_text") });
                return;
            }
            this.notifyError(err);
        },
        // ---------------- MDFStore sale-channel mapping ----------------
        // options are MDFStore platform channels only (platform_id = 9), served by BE.
        normalizeChannelId(v) {
            return typeof v === "number" && v > 0 ? v : null;
        },
        async loadMdfChannelMapping() {
            this.mdfChannelLoading = true;
            try {
                const res = await StorefrontService.getMdfstoreChannelMapping();
                // go-core envelope: Data (capital D) is an array; unwrap [0] (#281 / pattern per MemberList.vue:726).
                const list = (res.data && res.data.Data) || [];
                const data = list[0] || {};
                this.mdfChannelOptions = Array.isArray(data.options) ? data.options : [];
                this.mdfChannelForm = {
                    web_sale_channel_id: this.normalizeChannelId(data.web_sale_channel_id),
                    member_sale_channel_id: this.normalizeChannelId(data.member_sale_channel_id),
                    app_sale_channel_id: this.normalizeChannelId(data.app_sale_channel_id)
                };
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.mdfChannelLoading = false;
            }
        },
        async saveMdfChannelMapping() {
            this.mdfChannelSaving = true;
            try {
                await StorefrontService.updateMdfstoreChannelMapping({
                    web_sale_channel_id: this.mdfChannelForm.web_sale_channel_id || 0,
                    member_sale_channel_id: this.mdfChannelForm.member_sale_channel_id || 0,
                    app_sale_channel_id: this.mdfChannelForm.app_sale_channel_id || 0
                });
                this.$notify({ type: "success", message: this.$t("mdfstore_channel.saved") });
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.mdfChannelSaving = false;
            }
        },
        emptyHeroForm() {
            return {
                id: 0,
                image_key: "",
                image_url: "",
                link_url: "",
                caption_th: "",
                caption_en: "",
                sort_order: 0,
                enabled: true
            };
        },
        emptyPopupForm() {
            return {
                id: 0,
                title: "",
                image_key: "",
                image_url: "",
                link_url: "",
                show_on_www: true,
                show_on_member: true,
                sort_order: 0
            };
        },
        // ---------------- App version / force update (asrth/mdf-app#205) ----------------
        appStoreUrlPlaceholder(platform) {
            return platform === "android" ? APP_STORE_URL_ANDROID_DEFAULT : APP_STORE_URL_IOS_PLACEHOLDER;
        },
        async loadAppVersionSettings() {
            this.appVersionLoading = true;
            this.appVersionLoaded = false;
            this.appVersionLoadFailed = false;
            try {
                const res = await StorefrontService.getAppVersionSettings();
                const form = normalizeAppVersionResponse(res.data && res.data.data);
                this.appVersionForm = form;
                this.appVersionSaved = normalizeAppVersionResponse(res.data && res.data.data);
                this.appVersionErrors = {};
                this.appVersionLoaded = true;
            } catch (err) {
                this.appVersionLoadFailed = true;
                this.notifyError(err);
            } finally {
                this.appVersionLoading = false;
            }
        },
        async saveAppVersionSettings() {
            // S1: never PUT a form that did not load — it would clear every key.
            if (!this.appVersionCanSave) return;
            const errors = validateAppVersionForm(this.appVersionForm);
            this.appVersionErrors = errors;
            if (Object.keys(errors).length) return;

            const raised = raisedMinVersions(this.appVersionSaved, this.appVersionForm);
            if (raised.length) {
                const result = await swal.fire({
                    title: this.$t("app_version_settings.title"),
                    text: this.$t("app_version_settings.confirm_raise_min", { versions: raisedMinLabel(raised) }),
                    icon: "warning",
                    showCancelButton: true,
                    reverseButtons: true,
                    focusCancel: true,
                    confirmButtonText: this.$t("app_version_settings.btn_save"),
                    cancelButtonText: this.$t("storefront.btn_cancel"),
                    confirmButtonColor: "#d33",
                    cancelButtonColor: "#6c757d"
                });
                if (!result.value) return;
            }

            this.appVersionSaving = true;
            try {
                const res = await StorefrontService.updateAppVersionSettings(appVersionPayload(this.appVersionForm));
                const data = res.data && res.data.data;
                this.appVersionForm = normalizeAppVersionResponse(data);
                this.appVersionSaved = normalizeAppVersionResponse(data);
                this.$notify({ type: "success", message: this.$t("app_version_settings.saved") });
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.appVersionSaving = false;
            }
        },
        notifyError(err) {
            this.$notify({ type: "danger", message: apiErrorMessage(err, (k, p) => this.$t(k, p)) });
        },
        // ---------------- Hero ----------------
        async loadHero() {
            this.heroLoading = true;
            try {
                const res = await StorefrontService.listHero();
                const data = (res.data && res.data.data) || [];
                this.heroList = data
                    .map((s) => Object.assign({ _saving: false }, s))
                    .sort((a, b) => a.sort_order - b.sort_order);
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.heroLoading = false;
            }
        },
        openCreateHero() {
            this.heroEditMode = false;
            const maxOrder = this.heroList.reduce((m, s) => Math.max(m, s.sort_order || 0), 0);
            this.heroForm = Object.assign(this.emptyHeroForm(), { sort_order: this.heroList.length ? maxOrder + 1 : 0 });
            this.heroDialogVisible = true;
        },
        openEditHero(row) {
            this.heroEditMode = true;
            this.heroForm = {
                id: row.id,
                image_key: row.image_key || "",
                image_url: row.image_url || "",
                link_url: row.link_url || "",
                caption_th: row.caption_th || "",
                caption_en: row.caption_en || "",
                sort_order: row.sort_order || 0,
                enabled: !!row.enabled
            };
            this.heroDialogVisible = true;
        },
        resetHeroForm() {
            this.heroForm = this.emptyHeroForm();
            if (this.$refs.heroForm) {
                this.$refs.heroForm.clearValidate();
            }
        },
        submitHeroForm() {
            this.$refs.heroForm.validate(async (valid) => {
                if (!valid) return;
                this.heroSaving = true;
                try {
                    const payload = {
                        image_key: this.heroForm.image_key,
                        link_url: this.heroForm.link_url,
                        caption_th: this.heroForm.caption_th,
                        caption_en: this.heroForm.caption_en,
                        sort_order: this.heroForm.sort_order,
                        enabled: this.heroForm.enabled
                    };
                    if (this.heroEditMode) {
                        await StorefrontService.updateHero(this.heroForm.id, payload);
                    } else {
                        await StorefrontService.createHero(payload);
                    }
                    this.heroDialogVisible = false;
                    await this.loadHero();
                    this.$notify({ type: "success", message: this.$t("storefront.save_success") });
                } catch (err) {
                    this.notifyError(err);
                } finally {
                    this.heroSaving = false;
                }
            });
        },
        async toggleHeroEnabled(row) {
            row._saving = true;
            try {
                await StorefrontService.updateHero(row.id, { enabled: !row.enabled });
                await this.loadHero();
            } catch (err) {
                this.notifyError(err);
                row._saving = false;
            }
        },
        confirmDeleteHero(row) {
            swal.fire({
                title: this.$t("storefront.delete_title"),
                text: this.$t("storefront.delete_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("storefront.btn_delete"),
                cancelButtonText: this.$t("storefront.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await StorefrontService.removeHero(row.id);
                        await this.loadHero();
                        this.$notify({ type: "success", message: this.$t("storefront.delete_success") });
                    } catch (err) {
                        this.notifyError(err);
                    }
                }
            });
        },
        async moveUp(index) {
            if (index <= 0) return;
            await this.swapOrder(index, index - 1);
        },
        async moveDown(index) {
            if (index >= this.heroList.length - 1) return;
            await this.swapOrder(index, index + 1);
        },
        async swapOrder(a, b) {
            const slideA = this.heroList[a];
            const slideB = this.heroList[b];
            if (!slideA || !slideB) return;
            this.heroLoading = true;
            try {
                await StorefrontService.updateHero(slideA.id, { sort_order: slideB.sort_order });
                await StorefrontService.updateHero(slideB.id, { sort_order: slideA.sort_order });
                await this.loadHero();
            } catch (err) {
                this.notifyError(err);
                this.heroLoading = false;
            }
        },
        // ---------------- Popup (multi-row, single-active #59) ----------------
        async loadPopups() {
            this.popupLoading = true;
            try {
                const res = await StorefrontService.listPopup();
                const data = (res.data && res.data.data) || [];
                this.popupList = data
                    .map((p) => Object.assign({ _saving: false }, p))
                    .sort((a, b) => (a.sort_order - b.sort_order) || (a.id - b.id));
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.popupLoading = false;
            }
        },
        openCreatePopup() {
            this.popupEditMode = false;
            const maxOrder = this.popupList.reduce((m, p) => Math.max(m, p.sort_order || 0), 0);
            this.popupForm = Object.assign(this.emptyPopupForm(), { sort_order: this.popupList.length ? maxOrder + 1 : 0 });
            this.popupDialogVisible = true;
        },
        openEditPopup(row) {
            this.popupEditMode = true;
            this.popupForm = {
                id: row.id,
                title: row.title || "",
                image_key: row.image_key || "",
                image_url: row.image_url || "",
                link_url: row.link_url || "",
                show_on_www: !!row.show_on_www,
                show_on_member: !!row.show_on_member,
                sort_order: row.sort_order || 0
            };
            this.popupDialogVisible = true;
        },
        resetPopupForm() {
            this.popupForm = this.emptyPopupForm();
            if (this.$refs.popupForm) {
                this.$refs.popupForm.clearValidate();
            }
        },
        submitPopupForm() {
            this.$refs.popupForm.validate(async (valid) => {
                if (!valid) return;
                this.popupSaving = true;
                try {
                    const payload = {
                        title: this.popupForm.title,
                        image_key: this.popupForm.image_key,
                        link_url: this.popupForm.link_url,
                        show_on_www: this.popupForm.show_on_www,
                        show_on_member: this.popupForm.show_on_member,
                        sort_order: this.popupForm.sort_order
                    };
                    if (this.popupEditMode) {
                        await StorefrontService.updatePopup(this.popupForm.id, payload);
                    } else {
                        await StorefrontService.createPopup(payload);
                    }
                    this.popupDialogVisible = false;
                    await this.loadPopups();
                    this.$notify({ type: "success", message: this.$t("storefront.save_success") });
                } catch (err) {
                    this.notifyError(err);
                } finally {
                    this.popupSaving = false;
                }
            });
        },
        async activatePopup(row) {
            if (row.enabled) return;
            row._saving = true;
            try {
                await StorefrontService.activatePopup(row.id);
                await this.loadPopups();
                this.$notify({ type: "success", message: this.$t("storefront.activate_success") });
            } catch (err) {
                this.notifyError(err);
                row._saving = false;
            }
        },
        confirmDeletePopup(row) {
            swal.fire({
                title: this.$t("storefront.delete_title"),
                text: this.$t("storefront.delete_popup_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("storefront.btn_delete"),
                cancelButtonText: this.$t("storefront.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await StorefrontService.removePopup(row.id);
                        await this.loadPopups();
                        this.$notify({ type: "success", message: this.$t("storefront.delete_success") });
                    } catch (err) {
                        this.notifyError(err);
                    }
                }
            });
        }
    }
};
</script>

<style scoped>
/* termsParagraphs() keeps a SINGLE newline inside a paragraph (a wrapped
   sub-item of a numbered clause), so the preview has to honour it — without
   pre-line the browser collapses it into a space and the clause reads as one
   run-on line, which is exactly what the operator opened the preview to check. */
.coin-terms-preview p {
    white-space: pre-line;
    margin-bottom: 0.75rem;
}
</style>
